package com.evoapps.evoshift.domain.explain

import com.evoapps.evoshift.domain.model.FinancialSummary
import com.evoapps.evoshift.domain.model.FixedCost
import com.evoapps.evoshift.domain.model.LabourShare
import com.evoapps.evoshift.domain.model.MonthlyResult
import com.evoapps.evoshift.domain.model.MonthlyResultSide
import com.evoapps.evoshift.domain.model.PersonLabour
import com.evoapps.evoshift.domain.model.RosterFinancials
import com.evoapps.evoshift.domain.model.plusPeople
import com.evoapps.evoshift.domain.usecase.finance.DayTrend
import kotlin.math.abs

/*
 * SH-152: the month's figures explained (the Finances screen, the dashboard). The labour cost is
 * listed person by person from the days as they are worked out now ([DayTrend], the same days and
 * the same per-day choice of planned or actual shifts the shown totals are added up from:
 * withLiveLabour), with the people whose shifts are not counted (no pay set) and the unassigned
 * shifts left out. The income and the result come from the month's totals and its ledger, as the
 * cards show them ([MonthlyResult]).
 */
object MonthExplanations {

    /** The month's [side] of the totals: plan, actual to date, or the forecast (combined). */
    fun RosterFinancials.summaryOf(side: Side): FinancialSummary = when (side) {
        Side.PLAN -> planned
        Side.ACTUAL -> actual
        Side.FORECAST -> combined
    }

    /**
     * The labour cost of [side] ([shown], as on screen) person by person from [days]; people
     * whose shifts could not be priced and the unassigned shifts are listed, not counted. Without
     * days (not readable) the stored total is all there is.
     */
    fun labourCost(side: Side, shown: Double, days: List<DayTrend>): Breakdown {
        val counted = days.filter { it.isIn(side) }
        val people = counted.fold(emptyMap<String, PersonLabour>()) { sum, day -> sum.plusPeople(day.peopleOf(side)) }
        val unpriced = counted.fold(emptyMap<String, PersonLabour>()) { sum, day -> sum.plusPeople(day.unpricedPeople) }
        val excluded = counted.fold(emptyMap<String, PersonLabour>()) { sum, day -> sum.plusPeople(day.excludedPeople) }
        val openDays = if (side == Side.ACTUAL) emptyList() else days.filter { it.hoursOpen > 0 && (side == Side.PLAN || it.revenueActual == null) }
        val terms = buildList {
            addAll(personTerms(people))
            unpriced.entries.sortedBy { it.key }.forEach { (id, labour) ->
                add(Term(TermKind.PERSON_WITHOUT_PAY, 0.0, Sign.NOT_COUNTED, personId = id, hours = labour.hours, shifts = labour.shifts))
            }
            // SH-206: people who do not count in the labour figures: listed, not counted.
            excluded.entries.sortedBy { it.key }.forEach { (id, labour) ->
                add(Term(TermKind.PERSON_NOT_COUNTED, 0.0, Sign.NOT_COUNTED, personId = id, hours = labour.hours, shifts = labour.shifts))
            }
            // SH-193: unassigned shifts at the workplace's estimated rate (in the plan), else listed only.
            val priced = openDays.filter { it.openCostPerHour != null }
            val pricedHours = priced.sumOf { it.hoursOpen }
            if (pricedHours > 0) {
                val cost = priced.sumOf { it.openLabour }
                add(Term(TermKind.OPEN_SHIFTS, cost, hours = pricedHours, shifts = priced.sumOf { it.openShifts }, rate = cost / pricedHours))
            }
            val notPriced = openDays.filter { it.openCostPerHour == null }
            val openHours = notPriced.sumOf { it.hoursOpen }
            if (openHours > 0) add(Term(TermKind.OPEN_SHIFTS_NOT_PRICED, 0.0, Sign.NOT_COUNTED, hours = openHours, shifts = notPriced.sumOf { it.openShifts }.takeIf { it > 0 }))
        }
        return Breakdown.of(Figure.LABOUR_COST, side, shown, terms, residual = TermKind.SAVED_EARLIER)
    }

    /** People by cost (the largest first), each with their hours and shifts. */
    fun personTerms(people: Map<String, PersonLabour>): List<Term> =
        people.entries.sortedWith(compareByDescending<Map.Entry<String, PersonLabour>> { it.value.cost }.thenBy { it.key })
            .map { (id, labour) -> Term(TermKind.PERSON, labour.cost, personId = id, hours = labour.hours, shifts = labour.shifts) }

    /**
     * What [summary]'s labour % is measured against: its sales income (the commission on the net
     * sales, or the net sales without a commission) and the fixed revenue of the days it covers
     * ([daysCovered] of [daysInMonth]).
     */
    fun income(side: Side, summary: FinancialSummary, daysCovered: Int, daysInMonth: Int): Breakdown {
        val salesIncome = summary.salesIncomeAmount
        val fixed = summary.labourBaseAmount - salesIncome
        val terms = buildList {
            add(salesIncomeTerm(summary.revenue, summary.commission, salesIncome))
            if (abs(fixed) >= Breakdown.MONEY_EPSILON) {
                add(Term(TermKind.FIXED_REVENUE, fixed, days = daysCovered.takeIf { daysInMonth > 0 }, ofDays = daysInMonth.takeIf { it > 0 }))
            }
        }
        return Breakdown.of(Figure.INCOME, side, summary.labourBaseAmount, terms)
    }

    /**
     * The sales income: the commission ([commission] on [netSales], at its effective rate), the
     * net sales themselves without a commission, or both when the commission changed in the month.
     */
    fun salesIncomeTerm(netSales: Double, commission: Double, salesIncome: Double): Term = when {
        commission <= 0.0 -> Term(TermKind.NET_SALES_KEPT, salesIncome, base = netSales)
        abs(salesIncome - commission) < 0.01 ->
            Term(TermKind.COMMISSION, salesIncome, percent = if (netSales > 0) commission / netSales * 100 else null, base = netSales)
        else -> Term(
            TermKind.SALES_INCOME_MIXED, salesIncome, base = netSales,
            parts = listOf(Term(TermKind.COMMISSION, commission), Term(TermKind.NET_SALES_KEPT, salesIncome - commission))
        )
    }

    /** [summary]'s labour % (of income, or of sales without a commission): labour cost ÷ income × 100. */
    fun labourPercent(
        side: Side,
        summary: FinancialSummary,
        days: List<DayTrend>,
        daysCovered: Int,
        daysInMonth: Int
    ): Ratio = Ratio(
        Figure.LABOUR_PERCENT, side,
        labourCost(side, summary.laborCost, days),
        income(side, summary, daysCovered, daysInMonth)
    )

    /** The days [side] covers: the actual those with takings, the plan and the forecast all of them. */
    fun daysCovered(side: Side, financials: RosterFinancials): Int =
        if (side == Side.ACTUAL) financials.daysWithTakings else financials.days

    /** [financials]' labour % of [side], as the Finances header and cards show it. */
    fun labourPercent(side: Side, financials: RosterFinancials, days: List<DayTrend>): Ratio =
        labourPercent(side, financials.summaryOf(side), days, daysCovered(side, financials), financials.days)

    /**
     * The net sales of [side] ([shown]): the takings of the days with takings and the targets of
     * the rest (the forecast), the targets alone (the plan) or the takings alone (the actual).
     */
    fun netSales(side: Side, shown: Double, days: List<DayTrend>): Breakdown {
        val withTakings = days.filter { it.revenueActual != null }
        val terms = buildList {
            if (side != Side.PLAN && withTakings.isNotEmpty()) {
                add(Term(TermKind.TAKINGS, withTakings.sumOf { it.revenueActual ?: 0.0 }, days = withTakings.size))
            }
            val targets = when (side) {
                Side.PLAN -> days
                Side.ACTUAL -> emptyList()
                Side.FORECAST -> days.filter { it.revenueActual == null }
            }
            if (targets.isNotEmpty()) add(Term(TermKind.SALES_TARGETS, targets.sumOf { it.revenuePlan }, days = targets.size))
        }
        return Breakdown.of(Figure.NET_SALES, side, shown, terms, residual = TermKind.SAVED_EARLIER)
    }

    /** The labour cost and the other costs (the Finances cost structure's total). */
    fun totalExpenses(side: Side, summary: FinancialSummary, days: List<DayTrend>, ledger: List<FixedCost>): Breakdown =
        Breakdown.of(
            Figure.COSTS, side, summary.laborCost + summary.otherCosts,
            listOf(labourCost(side, summary.laborCost, days).asTerm(TermKind.LABOUR_COST), otherCosts(side, summary.otherCosts, ledger).asTerm(TermKind.OTHER_COSTS))
        )

    /** The month's ledger costs (the rules' fixed costs and the one-off expenses) item by item. */
    fun otherCosts(side: Side, shown: Double, ledger: List<FixedCost>): Breakdown =
        Breakdown.of(Figure.OTHER_COSTS, side, shown, ledgerTerms(ledger.filter { !it.isRevenue }))

    private fun ledgerTerms(items: List<FixedCost>): List<Term> =
        items.sortedWith(compareBy({ it.date }, { it.name })).map { Term(TermKind.LEDGER_ITEM, it.amount, name = it.name) }

    // --- The month's result (MonthlyResult), row by row --------------------------------------

    /** The rows of [result]'s [side] (the plan, the actual or the forecast) explained. */
    class ResultSide(
        val salesIncome: Breakdown,
        val fixedRevenue: Breakdown,
        val recurringRevenue: Breakdown,
        val oneOffRevenue: Breakdown,
        val revenue: Breakdown,
        val labourCost: Breakdown,
        val fixedCosts: Breakdown,
        val oneOffCosts: Breakdown,
        val sharedCosts: Breakdown,
        val costs: Breakdown,
        val result: Breakdown,
        val margin: Ratio
    )

    /**
     * [result]'s [side] row by row: each from the same side of the card ([MonthlyResultSide]), the
     * ledger's items by name, the labour cost person by person from [days].
     */
    fun resultSide(result: MonthlyResult, side: Side, ledger: List<FixedCost>, days: List<DayTrend>): ResultSide {
        val values: MonthlyResultSide = when (side) {
            Side.PLAN -> result.plan
            Side.ACTUAL -> result.actual
            Side.FORECAST -> result.forecast
        }
        val covered = if (side == Side.ACTUAL) result.daysWithTakings else result.days
        val hasLedger = ledger.isNotEmpty()
        fun items(oneOff: Boolean, revenue: Boolean, allocated: Boolean = false) =
            ledgerTerms(ledger.filter { it.isAllocated == allocated && (allocated || it.isOneOff == oneOff) && it.isRevenue == revenue })

        val salesIncome = Breakdown.of(
            Figure.SALES_INCOME, side, values.salesIncome,
            listOf(salesIncomeTerm(values.netSales, values.commission, values.salesIncome))
        )
        val fixedRevenue = Breakdown.of(
            Figure.FIXED_REVENUE, side, values.fixedRevenue,
            listOf(Term(TermKind.FIXED_REVENUE, values.fixedRevenue, days = covered.takeIf { result.days > 0 }, ofDays = result.days.takeIf { it > 0 }))
        )
        val recurringRevenue = Breakdown.of(Figure.RECURRING_REVENUE, side, values.recurringRevenue, if (hasLedger) items(oneOff = false, revenue = true) else emptyList())
        val oneOffRevenue = Breakdown.of(Figure.ONE_OFF_REVENUE, side, values.oneOffRevenue, if (hasLedger) items(oneOff = true, revenue = true) else emptyList())
        val revenue = Breakdown.of(
            Figure.REVENUE, side, values.revenue,
            listOf(
                salesIncomeTerm(values.netSales, values.commission, values.salesIncome),
                Term(TermKind.FIXED_REVENUE, values.fixedRevenue, days = covered.takeIf { result.days > 0 }, ofDays = result.days.takeIf { it > 0 }),
                recurringRevenue.asTerm(TermKind.RECURRING_REVENUE),
                oneOffRevenue.asTerm(TermKind.ONE_OFF_REVENUE)
            )
        )
        val labour = labourCost(side, values.labourCost, days)
        val fixedCosts = Breakdown.of(Figure.FIXED_COSTS, side, values.fixedCosts, if (hasLedger) items(oneOff = false, revenue = false) else emptyList())
        val oneOffCosts = Breakdown.of(Figure.ONE_OFF_COSTS, side, values.oneOffCosts, if (hasLedger) items(oneOff = true, revenue = false) else emptyList())
        val sharedCosts = Breakdown.of(Figure.SHARED_COSTS, side, values.sharedCosts, items(oneOff = false, revenue = false, allocated = true))
        val costs = Breakdown.of(
            Figure.COSTS, side, values.costs,
            listOf(
                labour.asTerm(TermKind.LABOUR_COST),
                fixedCosts.asTerm(TermKind.FIXED_COSTS),
                oneOffCosts.asTerm(TermKind.ONE_OFF_COSTS),
                sharedCosts.asTerm(TermKind.SHARED_COSTS)
            )
        )
        val resultBreakdown = Breakdown.of(
            Figure.RESULT, side, values.result,
            listOf(revenue.asTerm(TermKind.REVENUE), costs.asTerm(TermKind.COSTS, Sign.MINUS))
        )
        val margin = Ratio(Figure.MARGIN, side, resultBreakdown, revenue)
        return ResultSide(salesIncome, fixedRevenue, recurringRevenue, oneOffRevenue, revenue, labour, fixedCosts, oneOffCosts, sharedCosts, costs, resultBreakdown, margin)
    }

    // --- From the days alone (the dashboard) -------------------------------------------------

    /**
     * The month's labour % from its [days] as the dashboard adds it up (SH-147): the days' labour
     * (the forecast's choice) ÷ the days' income (their sales income and fixed revenue shares).
     */
    fun labourPercentOfDays(days: List<DayTrend>): Ratio {
        val labour = days.sumOf { it.labourOf(Side.FORECAST) }
        val sales = days.sumOf { it.sales }
        val salesIncome = days.sumOf { LabourShare.salesIncome(it.sales, it.commissionPercent) }
        val commission = days.filter { it.commissionPercent > 0 }.sumOf { it.sales * it.commissionPercent }
        val fixed = days.sumOf { it.fixedRevenue }
        val income = days.sumOf { it.income }
        val incomeTerms = buildList {
            add(salesIncomeTerm(sales, commission, salesIncome))
            if (fixed != 0.0) add(Term(TermKind.FIXED_REVENUE, fixed, days = days.size, ofDays = days.size))
        }
        return Ratio(
            Figure.LABOUR_PERCENT, Side.FORECAST,
            labourCost(Side.FORECAST, labour, days),
            Breakdown.of(Figure.INCOME, Side.FORECAST, income, incomeTerms)
        )
    }
}
